import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Upload, Loader2, FileText, Trash2, Cpu, Eye, RefreshCw, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { useConfirm } from './useConfirm';
import { errorCodeLibrariesApi } from '../services/api';

/**
 * Error Code Libraries admin panel.
 *
 * Lets an admin upload an OEM service-manual PDF, then watches the backend
 * extract every fault code into a structured library via Gemini. Once a
 * library is `ready` it can be linked to assets so the Public QR + Operator
 * app can look codes up in the field.
 */
const STATUS_STYLES = {
  uploaded:   { label: 'Queued',     class: 'bg-zinc-700 text-zinc-200', icon: Loader2 },
  extracting: { label: 'Extracting', class: 'bg-amber-500/20 text-amber-400 border border-amber-500/30', icon: Loader2 },
  ready:      { label: 'Ready',      class: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30', icon: CheckCircle2 },
  failed:     { label: 'Failed',     class: 'bg-red-500/20 text-red-400 border border-red-500/30', icon: AlertTriangle },
};

export const ErrorCodeLibrariesCard = () => {
  const { confirm, dialogProps } = useConfirm();
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showCodes, setShowCodes] = useState(null); // library object
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await errorCodeLibrariesApi.list();
      setLibraries(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load error-code libraries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Poll while any library is still extracting — the backend runs Gemini
  // in a background task and status flips uploaded → extracting → ready.
  useEffect(() => {
    const active = libraries.some(l => l.status === 'uploaded' || l.status === 'extracting');
    if (!active) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(load, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [libraries]);

  const handleDelete = (lib) => {
    confirm({
      title: `Delete "${lib.name}"?`,
      description: 'This removes the library, all extracted codes, and unlinks it from every asset.',
      testId: 'confirm-delete-error-code-library',
      onConfirm: async () => {
        try {
          await errorCodeLibrariesApi.delete(lib.id);
          toast.success(`Deleted "${lib.name}"`);
          setLibraries(prev => prev.filter(l => l.id !== lib.id));
        } catch {
          toast.error('Delete failed');
          throw new Error('delete failed');
        }
      },
    });
  };

  return (
    <Card data-testid="error-code-libraries-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="uppercase tracking-wide flex items-center gap-2">
            <Cpu className="w-5 h-5" />
            Error Code Libraries
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1.5">
            Upload OEM service manual PDFs. Guardian extracts every fault code
            with Gemini AI so operators can look them up via the QR page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ecl-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setShowUpload(true)} data-testid="ecl-upload-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload manual
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : libraries.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-8 text-center" data-testid="ecl-empty">
            <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No libraries yet. Upload your first OEM PDF to start the extraction.
            </p>
          </div>
        ) : (
          <div className="rounded-md border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Make / Model</TableHead>
                  <TableHead className="text-right">Codes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {libraries.map(lib => {
                  const s = STATUS_STYLES[lib.status] || STATUS_STYLES.uploaded;
                  const StatusIcon = s.icon;
                  const spin = lib.status === 'uploaded' || lib.status === 'extracting';
                  return (
                    <TableRow key={lib.id} data-testid={`ecl-row-${lib.id}`}>
                      <TableCell className="font-medium">
                        {lib.name}
                        <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[260px]">
                          {lib.source_pdf_filename}
                        </p>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {[lib.make, lib.model].filter(Boolean).join(' · ') || '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {lib.status === 'ready' ? lib.code_count : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${s.class} border-0 gap-1`}>
                          <StatusIcon className={`w-3 h-3 ${spin ? 'animate-spin' : ''}`} />
                          {s.label}
                        </Badge>
                        {lib.status === 'failed' && lib.error_message && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="ml-2 text-xs text-red-400 underline">why?</TooltipTrigger>
                              <TooltipContent className="max-w-sm">{lib.error_message}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={lib.status !== 'ready'}
                            onClick={() => setShowCodes(lib)}
                            data-testid={`ecl-view-${lib.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(lib)}
                            data-testid={`ecl-delete-${lib.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Linking happens on the Asset detail drawer — point users there. */}
        <div className="text-xs text-muted-foreground mt-4 leading-relaxed">
          <strong className="text-foreground">Tip:</strong> After a library is{' '}
          <Badge className={`${STATUS_STYLES.ready.class} border-0 gap-1 inline-flex align-middle`}>
            <CheckCircle2 className="w-3 h-3" />Ready
          </Badge>
          , open an asset and use the <em>Linked OEM Manuals</em> card to attach it.
          The Public QR page and Operator app will then offer code lookup for that asset.
        </div>
      </CardContent>

      {showUpload && (
        <UploadDialog
          onClose={() => setShowUpload(false)}
          onCreated={(lib) => {
            setShowUpload(false);
            setLibraries(prev => [lib, ...prev]);
            toast.success(`Uploaded "${lib.name}" — extraction queued`);
          }}
        />
      )}

      {showCodes && (
        <CodesDialog library={showCodes} onClose={() => setShowCodes(null)} />
      )}
      <ConfirmDeleteDialog {...dialogProps} />
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Upload dialog
// ─────────────────────────────────────────────────────────────────────────────
const UploadDialog = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please pick a PDF file');
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error('PDF must be under 50 MB');
      return;
    }
    setFile(f);
    if (!name) {
      // Default the library name to the filename minus extension.
      setName(f.name.replace(/\.pdf$/i, ''));
    }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Give the library a name'); return; }
    if (!file)        { toast.error('Pick a PDF file'); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onerror = () => { setUploading(false); toast.error('Could not read file'); };
      reader.onload = async () => {
        try {
          const res = await errorCodeLibrariesApi.create({
            name: name.trim(),
            make: make.trim() || null,
            model: model.trim() || null,
            file_data: reader.result,
            filename: file.name,
          });
          onCreated(res.data);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : 'Upload failed');
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false);
      toast.error('Upload failed');
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !uploading && onClose()}>
      <DialogContent data-testid="ecl-upload-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload OEM service manual</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="ecl-name">Library name *</Label>
            <Input
              id="ecl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. CAT 320D Service Manual"
              data-testid="ecl-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ecl-make">Make</Label>
              <Input id="ecl-make" value={make} onChange={(e) => setMake(e.target.value)} placeholder="Caterpillar" data-testid="ecl-make-input" />
            </div>
            <div>
              <Label htmlFor="ecl-model">Model</Label>
              <Input id="ecl-model" value={model} onChange={(e) => setModel(e.target.value)} placeholder="320D" data-testid="ecl-model-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="ecl-file">Service manual PDF *</Label>
            <Input id="ecl-file" type="file" accept="application/pdf" onChange={handleFile} data-testid="ecl-file-input" />
            {file && (
              <p className="text-xs text-muted-foreground mt-1">
                {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
              </p>
            )}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Extraction runs in the background and usually takes 1–3 minutes for a typical
            500-page manual. Status updates live on the table.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
          <Button onClick={submit} disabled={uploading || !file || !name.trim()} data-testid="ecl-upload-submit-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload & extract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Browse codes inside a single library
// ─────────────────────────────────────────────────────────────────────────────
const CodesDialog = ({ library, onClose }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const all = [];
        for (let offset = 0; ; offset += 100) {
          const res = await errorCodeLibrariesApi.listCodes(library.id, { limit: 100, offset });
          const items = res.data?.items || [];
          all.push(...items);
          if (items.length < 100 || all.length >= (res.data?.total || 0)) break;
        }
        setCodes(all);
      } catch {
        toast.error('Failed to load codes');
      } finally {
        setLoading(false);
      }
    })();
  }, [library.id]);

  const filtered = q
    ? codes.filter(c =>
        (c.code || '').toLowerCase().includes(q.toLowerCase()) ||
        (c.title || '').toLowerCase().includes(q.toLowerCase()))
    : codes;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="ecl-codes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cpu className="w-4 h-4" /> {library.name}
            <Badge className="bg-zinc-700 text-zinc-200 border-0 ml-2">{codes.length} codes</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <Input
            placeholder="Filter by code or title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="ecl-codes-filter-input"
          />
        </div>
        <div className="flex-1 overflow-y-auto rounded-md border border-border">
          {loading ? (
            <p className="text-sm text-muted-foreground p-4">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">No codes match "{q}"</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Code</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-[110px]">Severity</TableHead>
                  <TableHead className="w-[70px] text-right">Page</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-volt">{c.code}</TableCell>
                    <TableCell>{c.title || '—'}</TableCell>
                    <TableCell>
                      {c.severity ? (
                        <Badge className="bg-zinc-700 text-zinc-200 border-0 uppercase text-[10px]">
                          {c.severity}
                        </Badge>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {c.source_page ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
